import { useRef } from 'react';

/**
 * Returns the PREVIOUS value whenever the next one is shallow-equal to it, so a caller
 * passing an object literal inline does not hand every downstream memo a new identity
 * each render.
 *
 * Used for the pass-through prop bags on DS components (`segmentNativeProps`,
 * `segmentWebProps`, …). Those are almost always written inline at the call site —
 * `segmentNativeProps={{ testID: 'x' }}` — which, listed as a memo dependency, silently
 * turns that memo into pure overhead. Latching on shallow equality means the memo-
 * sensitivity of the prop is the component's problem rather than a rule every call site
 * has to know about.
 */
export function useShallowStable<T extends object | undefined>(next: T): T {
  const ref = useRef<T>(next);
  const prev = ref.current;

  if (prev === next) return prev;
  if (prev === undefined || next === undefined) {
    ref.current = next;
    return next;
  }

  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length === nextKeys.length) {
    let same = true;
    for (const key of prevKeys) {
      if (
        !Object.prototype.hasOwnProperty.call(next, key) ||
        (prev as Record<string, unknown>)[key] !== (next as Record<string, unknown>)[key]
      ) {
        same = false;
        break;
      }
    }
    if (same) return prev;
  }

  ref.current = next;
  return next;
}
